import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle, Circle, PencilSimple } from "@phosphor-icons/react";

export const PROJECT_STATUSES = [
  { key: "onboarding", label: "Onboarding", hint: "Client tasks in progress" },
  { key: "working", label: "Working on Project", hint: "All client tasks complete" },
  { key: "edits", label: "Edit Phase", hint: "Sample delivered — awaiting feedback" },
  { key: "active_management", label: "Active Management", hint: "Marketing is being actively managed" },
  { key: "paused", label: "Paused Management", hint: "Marketing management is on hold" },
  { key: "offboarding", label: "Offboarding", hint: "Wrapping up" },
  { key: "completed", label: "Completed", hint: "Closed out" },
];

const COLORS = {
  onboarding: "#6366f1", // indigo
  working: "#0ea5e9",    // sky
  edits: "#d97706",      // amber
  active_management: "#0ea5e9",
  paused: "#d97706",
  offboarding: "#059669", // emerald
  completed: "#64748b",   // slate — subtle terminal for branding/unmarked
};

const DESIGN_TRACK = ["onboarding", "working", "edits", "offboarding", "completed"];
const BRANDING_TRACK = ["onboarding", "working", "edits", "completed"];
const MARKETING_TRACK = ["onboarding", "active_management", "paused", "offboarding", "completed"];

// Web design takes priority; marketing-only clients get the management track.
function trackFor(types) {
  const t = types.map(x => (x || "").toLowerCase());
  if (t.includes("web_design")) return DESIGN_TRACK;
  if (t.includes("marketing")) return MARKETING_TRACK;
  return BRANDING_TRACK;
}

export function isProjectStatusEligible(client) {
  // All clients participate in the lifecycle. Branding & unmarked clients
  // terminate at "completed"; web_design & marketing at "offboarding".
  return !!client;
}

/**
 * ProjectStatusBox — displays the current project lifecycle status.
 * When `editable` is true, the admin can override the status via a dropdown.
 * Auto-transitions happen server-side (task completion, sample sent, approval).
 */
export default function ProjectStatusBox({ client, userId, editable = false, onSaved, testid = "project-status" }) {
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [pausing, setPausing] = useState(false);
  const [reason, setReason] = useState("");
  const status = client?.project_status || "onboarding";
  // Pick the lifecycle track by client type: web_design/marketing → ends in
  // offboarding; branding/unmarked → ends in completed. We hide the terminal
  // the client won't reach so the progress indicator reads cleanly.
  const types = (Array.isArray(client?.client_types) && client.client_types.length > 0)
    ? client.client_types
    : [client?.client_type || ""];
  const visibleStatuses = PROJECT_STATUSES.filter(s => trackFor(types).includes(s.key));
  const currentIdx = visibleStatuses.findIndex(s => s.key === status);
  const current = visibleStatuses[currentIdx] || visibleStatuses[0];
  const color = COLORS[current.key] || COLORS.onboarding;

  const save = async (newStatus) => {
    if (newStatus === status) { setEditing(false); return; }
    if (newStatus === "paused" && !pausing) { setPausing(true); return; }
    setSaving(true);
    try {
      const payload = { project_status: newStatus };
      if (newStatus === "paused") payload.pause_reason = reason.trim();
      await api.patch(`/admin/clients/${userId}`, payload);
      toast.success(`Status → ${PROJECT_STATUSES.find(s => s.key === newStatus)?.label}`);
      setEditing(false); setPausing(false); setReason("");
      if (onSaved) onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="panel p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <p className="label">Project status</p>
        {editable && !editing && (
          <div className="flex items-center gap-2">
            <button onClick={() => setEditing(true)}
              data-testid={`${testid}-edit`}
              className="btn-ghost text-xs">
              <PencilSimple size={11} weight="bold" /> Edit
            </button>
          </div>
        )}
      </div>

      {!editing ? (
        <>
          <div className="flex items-center gap-2">
            <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: color }} />
            <p className="title-xl text-xl" data-testid={`${testid}-current`}>{current.label}</p>
          </div>
          <p className="text-xs mt-1" style={{ color: "var(--text-dim)" }}>{current.hint}</p>
          {status === "paused" && client?.pause_reason && (
            <p className="text-sm mt-2 px-3 py-2 hair" data-testid={`${testid}-pause-reason`}
              style={{ border: "1px solid var(--hair)" }}>
              <span className="label block mb-0.5">Reason</span>{client.pause_reason}
            </p>
          )}

          {/* Progress track */}
          <div className="mt-4 space-y-2">
            {visibleStatuses.map((s, i) => {
              const done = i < currentIdx;
              const active = i === currentIdx;
              return (
                <div key={s.key} className="flex items-center gap-2 text-xs"
                  data-testid={`${testid}-step-${s.key}`}
                  style={{ opacity: done ? 0.55 : 1 }}>
                  {done ? (
                    <CheckCircle size={14} weight="fill" style={{ color }} />
                  ) : (
                    <Circle size={14} weight={active ? "fill" : "regular"}
                      style={{ color: active ? color : "var(--text-dim)" }} />
                  )}
                  <span style={{ fontWeight: active ? 700 : 400 }}>{s.label}</span>
                </div>
              );
            })}
          </div>
        </>
      ) : pausing ? (
        <div className="space-y-2" data-testid={`${testid}-pause-form`}>
          <p className="text-sm font-medium">Pause management</p>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
            placeholder="Reason (optional). Included in the client's email."
            data-testid={`${testid}-pause-reason-input`} className="w-full px-3 py-2 text-sm" />
          <button onClick={() => save("paused")} disabled={saving}
            data-testid={`${testid}-pause-confirm`} className="btn-primary text-xs w-full justify-center">
            {saving ? "Pausing…" : "Pause & notify client"}
          </button>
          <button onClick={() => { setPausing(false); setReason(""); }} disabled={saving}
            data-testid={`${testid}-pause-cancel`} className="btn-ghost text-xs w-full">
            Back
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {visibleStatuses.map((s) => (
            <button key={s.key} onClick={() => save(s.key)} disabled={saving}
              data-testid={`${testid}-set-${s.key}`}
              className="w-full text-left px-3 py-2 text-sm hair"
              style={{
                background: s.key === status ? "var(--text)" : "transparent",
                color: s.key === status ? "var(--bg)" : "var(--text)",
                border: "1px solid var(--hair)",
              }}>
              <div className="flex items-center gap-2">
                <span className="inline-block w-2 h-2 rounded-full" style={{ background: COLORS[s.key] }} />
                <span className="font-medium">{s.label}</span>
              </div>
              <p className="text-xs mt-0.5"
                style={{ color: s.key === status ? "var(--bg)" : "var(--text-dim)" }}>
                {s.hint}
              </p>
            </button>
          ))}
          <button onClick={() => setEditing(false)} disabled={saving}
            data-testid={`${testid}-cancel`}
            className="btn-ghost text-xs w-full">
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}
